<script setup>
import {onMounted, ref, watch} from 'vue'
import {codePreview, formatDate, showSuccessMessage, validatePhoneNumber} from '@/helper';
import {Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import Textarea from '@/components/forms/Textarea.vue';
import Select from '@/components/forms/Select.vue';
import {authData} from '@/services/AuthService';
import Input from "@/components/forms/Input.vue";
import {createConfirmDialog} from "vuejs-confirm-dialog";
import ModalDialog from "@/components/modals/ModalDialog.vue";
import {useI18n} from "vue-i18n";
import FormTemplateService from "@/services/FormTemplateService";
import ClientService from "@/services/ClientService";
import SettingsService from "@/services/SettingsService";
import DocumentTemplateService from "@/services/DocumentTemplateService";

const {t} = useI18n();

const props = defineProps({
  clientId: {type: Number, required: true},
});

const onboardingOptions = ref({investment_questionnaires: [], aml_questionnaires: [], forms: [], meeting_records: [], identification: false, client_complex_form: false, address: false, bank_account: false, documents: false, strategies: false});
const investmentQuestionnaires = ref([]);
const amlQuestionnaires = ref([]);
const forms = ref([]);
const meetingRecords = ref([]);
const clientContracts = ref([]);
const documentsTemplates = ref([]);

const isOpen = ref(true)
const emit = defineEmits(['confirm', 'cancel']);

onMounted(async () => {
  investmentQuestionnaires.value = await getFormsTemplatesByType('INVESTMENT_QUESTIONNAIRE');

  amlQuestionnaires.value = await getFormsTemplatesByType('AML_QUESTIONNAIRE');

  forms.value = await getFormsTemplatesByType('FORM');

  meetingRecords.value = await getFormsTemplatesByType('MEETING_RECORD');

  getClientContracts();

  await getActiveDocumentsTemplates();
});

async function getFormsTemplatesByType(type) {
  const formTemplates = await FormTemplateService.getAllByType(type, true);
  return formTemplates;
}

function getClientContracts() {
  ClientService.getClientContracts(props.clientId).then(response => {
        clientContracts.value = response;
      }
  )
}

function getSettingsValueByKey(key) {
  return SettingsService.getSettingsValueByKey(key)
      .then((response) => {
        return response[key];
      })
      .catch((error) => {
      });
}

async function getActiveDocumentsTemplates() {
  documentsTemplates.value = await DocumentTemplateService.getAllActive();
}

const closeModalWith = (value) => {
  setTimeout(() => {
    if (value === 'confirm') {
      emit('confirm', onboardingOptions.value);
      isOpen.value = false;
    } else {
      emit('cancel');
      isOpen.value = false;
    }
  }, 300);
}

function openModal() {
  isOpen.value = true
}

</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" class="modal show d-block" style="z-index: 1055;">
      <div class="modal-backdrop fade show"></div>
      <div class="modal-dialog modal-dialog-centered modal-lg" style="z-index: 1055">
        <div class="modal-content">
          <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
            <div class="modal-header">
              <DialogTitle as="h3" class="modal-title">
                {{ t('clientsDetail.onboarding_options') }}
              </DialogTitle>
              <i class="btn-close cursor-pointer" @click="closeModalWith('cancel')" aria-label="Close"></i>
            </div>
            <form @submit.prevent="closeModalWith('confirm')">
              <div class="modal-body">
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="parentId" class="form-label">{{ t('onboardingOptionsModal.investment_questionnaire') }}</label>
                    <select class="form-select" v-model="onboardingOptions.investment_questionnaires">
                      <option v-for="(investmentQuestionnaire, index) in investmentQuestionnaires" :key="index" :value="[investmentQuestionnaire.id]">{{ investmentQuestionnaire.name }}</option>
                    </select>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="parentId" class="form-label">{{ t('onboardingOptionsModal.aml_questionnaire') }}</label>
                    <select class="form-select" v-model="onboardingOptions.aml_questionnaires">
                      <option v-for="(amlQuestionnaire, index) in amlQuestionnaires" :key="index" :value="[amlQuestionnaire.id]">{{ amlQuestionnaire.name }}</option>
                    </select>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="parentId" class="form-label">{{ t('onboardingOptionsModal.forms') }}</label>
                    <select class="form-select" v-model="onboardingOptions.forms" multiple>
                      <option v-for="(form, index) in forms" :key="index" :value="form.id">{{ form.name }}</option>
                    </select>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="parentId" class="form-label">{{ t('onboardingOptionsModal.meeting_record') }}</label>
                    <select class="form-select" v-model="onboardingOptions.meeting_records">
                      <option v-for="(meetingRecord, index) in meetingRecords" :key="index" :value="[meetingRecord.id]">{{ meetingRecord.name }}</option>
                    </select>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="form-group">
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" role="switch" id="switchToIdentify" v-model="onboardingOptions.identification"/>
                      <label class="form-check-label" for="switchToIdentify">{{ t('onboardingOptionsModal.identification') }}</label>
                    </div>
                  </div>
                  <div class="form-group">
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" role="switch" id="switchFillData" v-model="onboardingOptions.client_complex_form"/>
                      <label class="form-check-label" for="switchFillData">{{ t('onboardingOptionsModal.client_complex_form') }}</label>
                    </div>
                  </div>
                  <div class="form-group">
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" role="switch" id="switchFillAddress" v-model="onboardingOptions.address"/>
                      <label class="form-check-label" for="switchFillAddress">{{ t('onboardingOptionsModal.address') }}</label>
                    </div>
                  </div>
                  <div class="form-group">
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" role="switch" id="switchFillBankAccount" v-model="onboardingOptions.bank_account"/>
                      <label class="form-check-label" for="switchFillBankAccount">{{ t('onboardingOptionsModal.bank_account') }}</label>
                    </div>
                  </div>
                  <div class="form-group">
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" role="switch" id="switchDocuments" v-model="onboardingOptions.documents"/>
                      <label class="form-check-label" for="switchDocuments">{{ t('onboardingOptionsModal.documents') }}</label>
                    </div>
                  </div>
                  <div class="form-group">
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" role="switch" id="switchCalculation" v-model="onboardingOptions.calculation"/>
                      <label class="form-check-label" for="switchCalculation">{{ t('onboardingOptionsModal.calculation') }}</label>
                    </div>
                  </div>
                  <div class="form-group">
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" role="switch" id="switchStrategyChoice" v-model="onboardingOptions.strategies"/>
                      <label class="form-check-label" for="switchStrategyChoice">{{ t('onboardingOptionsModal.strategies_choice') }}</label>
                    </div>
                  </div>
                </div>

                <div v-if="onboardingOptions.strategies || onboardingOptions.calculation" class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="parentId" class="form-label">{{ t('onboardingOptionsModal.client_contracts') }}</label>
                    <select class="form-select" v-model="onboardingOptions.contract">
                      <option v-for="(clientContract, index) in clientContracts" :key="index" :value="clientContract.id">{{ clientContract.contractType + ' (' + clientContract.contract_number + ')' }}</option>
                    </select>
                  </div>
                </div>
                <!-- Šablony dokumentů -->
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="parentId" class="form-label">{{ t('onboardingOptionsModal.generate_documents_w8') }} </label>
                    <select class="form-select" v-model="onboardingOptions.documents_templates_w8_ids" multiple>
                      <option value="[]">---</option>
                      <option v-for="(documentTemplate, index) in documentsTemplates" :key="index" :value="documentTemplate.id">{{ documentTemplate.name }}</option>
                    </select>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="parentId" class="form-label">{{ t('onboardingOptionsModal.generate_documents_others') }} </label>
                    <select class="form-select" v-model="onboardingOptions.documents_templates_ids" multiple>
                      <option value="[]">---</option>
                      <option v-for="(documentTemplate, index) in documentsTemplates" :key="index" :value="documentTemplate.id">{{ documentTemplate.name }}</option>
                    </select>
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <button class="btn btn-link text-decoration-underline text-dark" @click="closeModalWith('cancel')">{{ t('onboardingOptionsModal.cancel') }}</button>
                <button class="btn btn-primary btn-md" type="submit">{{ t('onboardingOptionsModal.save') }}</button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
